// @flow

import * as React from 'react';
import ButtonBase from '@material-ui/core/ButtonBase';
import { Flex, Box, useSystem } from 'react-system';
import { useTheme } from '../theme';

type GroupProps = {|
  children: React.Node,
  disabled?: boolean,
  responsive?: boolean,
|};

type CardProps = {|
  checked: boolean,
  icon?: React.Node,
  children: React.Node,
  disabled?: boolean,
  onChange: () => void,
  responsive?: boolean,
|};

const RadioCardContext = React.createContext(null);

export const RadioCardGroup = ({
  children,
  disabled,
  responsive = true,
}: GroupProps): React.Node => {
  const { media } = useSystem();

  return (
    <Flex mx={-2}>
      <Box
        css={media({
          display: 'flex',
          width: '100%',
          flexWrap: responsive ? ['wrap', 'nowrap'] : 'wrap',
        })}
      >
        <RadioCardContext.Provider value={{ disabled, responsive }}>
          {children}
        </RadioCardContext.Provider>
      </Box>
    </Flex>
  );
};

export const RadioCard = ({
  checked,
  onChange,
  icon,
  children,
}: CardProps): React.Node => {
  const context = React.useContext(RadioCardContext);
  const { text, colors, textColor, bgColor } = useTheme();
  const { media } = useSystem();

  if (context == null) {
    throw Error('RadioCard should have provider in the tree');
  }

  return (
    <ButtonBase
      disabled={context.disabled}
      component={Box}
      width={1 / 2}
      m={context.responsive ? [1, 2] : 1}
      py={context.responsive ? [3, icon != null ? 4 : 3] : 3}
      px={context.responsive ? [4, 3] : 4}
      css={[
        bgColor(checked ? 'primaryMain' : 'white'),
        media({
          flexDirection: context.responsive ? ['row', 'column'] : 'row',
          border: `1px solid ${colors.divider}`,
          justifyContent: [icon != null ? 'flex-start' : 'center', 'unset'],
          borderRadius: 5,
          width: context.responsive ? ['100%', '50%'] : '100%',
        }),
      ]}
      focusRipple={true}
      onClick={onChange}
    >
      {icon != null && (
        <Box
          pb={context.responsive ? [0, 3] : 0}
          pr={context.responsive ? [3, 0] : 3}
          css={[textColor(checked ? 'white' : 'primaryMain')]}
        >
          {icon}
        </Box>
      )}

      <Box
        css={[
          text.button,
          textColor(checked ? 'white' : 'highText'),
          { textAlign: 'center' },
        ]}
      >
        {children}
      </Box>
    </ButtonBase>
  );
};
